import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  CircularProgress,
  Box,
  Chip,
  Card,
  Pagination,
  Stack,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";
import api from "../../utils/api";

const ITEMS_PER_PAGE = 10;

const GateVisitorsList = ({ clubMemberCode }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchVisitors = async () => {
      if (!clubMemberCode) {
        setVisits([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await api.get(`/gate-visitors/member/${clubMemberCode}`);
        const sorted = (res.data || []).sort(
          (a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt)
        );
        setVisits(sorted);
      } catch (err) {
        console.error("Failed to fetch gate visitors:", err);
        setVisits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVisitors();
  }, [clubMemberCode]);

  useEffect(() => {
    setPage(1);
  }, [visits]);

  const formatDateTime = (date) =>
    date ? new Date(date).toLocaleString() : "—";

  const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return "success";
      case "denied":
        return "error";
      case "checked-in":
        return "primary";
      case "checked-out":
        return "info";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };

  const totalPages = Math.ceil((visits?.length || 0) / ITEMS_PER_PAGE);

  const paginatedVisits = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return (visits || []).slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [visits, page]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (visits.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No gate visitors found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          This member does not currently have any recorded gate visitor activity.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Stack spacing={2.25}>
        {paginatedVisits.map((v) => {
          const visitorName =
            `${v.FirstName || ""} ${v.LastName || ""}`.trim() || "Unknown Visitor";

          return (
            <Card
              key={v.VisitID}
              sx={{
                ...pageCardSx,
                p: { xs: 1.5, sm: 2, md: 2.25 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(47,99,173,0.08)",
                        flexShrink: 0,
                      }}
                    >
                      <NotesIcon sx={{ color: "#2f63ad" }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: "#223142",
                          lineHeight: 1.2,
                        }}
                      >
                        {visitorName}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b778c",
                          mt: 0.25,
                        }}
                      >
                        {v.Description || "No description available"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1,
                      pl: { xs: 0, sm: "52px" },
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Arrival:
                      </Box>{" "}
                      {formatDateTime(v.ArrivalDate)}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Departure:
                      </Box>{" "}
                      {formatDateTime(v.DepartureDate)}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Authorized By:
                      </Box>{" "}
                      {v.AuthorizedBy || "—"}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Created At:
                      </Box>{" "}
                      {formatDateTime(v.CreatedAt)}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Chip
                    label={v.StatusName || "Unknown"}
                    color={getStatusColor(v.StatusName)}
                    sx={{
                      fontWeight: 700,
                      color: "#fff",
                      height: 32,
                      borderRadius: "999px",
                      minWidth: 110,
                    }}
                  />
                </Box>
              </Box>
            </Card>
          );
        })}
      </Stack>

      {visits.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(page * ITEMS_PER_PAGE, visits.length)} of {visits.length} visitors
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GateVisitorsList;